import type { Breadcrumb, Event, SpanJSON } from '@sentry/core';

const SCRUBBED_SIGNED_URL_QUERY = 'signed-url-scrubbed=true';

const SIGNED_URL_PARAM_NAMES = new Set([
  'awsaccesskeyid',
  'expires',
  'googleaccessid',
  'key-pair-id',
  'policy',
  'signature',
  'x-amz-algorithm',
  'x-amz-content-sha256',
  'x-amz-credential',
  'x-amz-date',
  'x-amz-expires',
  'x-amz-security-token',
  'x-amz-signature',
  'x-amz-signedheaders',
  'x-goog-algorithm',
  'x-goog-credential',
  'x-goog-date',
  'x-goog-expires',
  'x-goog-security-token',
  'x-goog-signature',
  'x-goog-signedheaders',
]);

const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>)]*/gi;

export function scrubSignedUrlsFromSentryEvent<T extends Event>(event: T): T {
  return scrubSignedUrls(event) as T;
}

export function scrubSignedUrlsFromSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  return scrubSignedUrls(breadcrumb) as Breadcrumb;
}

export function scrubSignedUrlsFromSentrySpan(span: SpanJSON): SpanJSON {
  return scrubSignedUrls(span) as SpanJSON;
}

export function scrubSignedUrls<T>(value: T): T {
  return scrubSignedUrlsRecursive(value, 0) as T;
}

function scrubSignedUrlsRecursive(value: unknown, depth: number): unknown {
  if (depth > 20) {
    return value;
  }

  if (typeof value === 'string') {
    return scrubSignedUrlsFromString(value);
  }

  if (Array.isArray(value)) {
    return value.map(item => scrubSignedUrlsRecursive(item, depth + 1));
  }

  if (!isPlainRecord(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, scrubSignedUrlsRecursive(item, depth + 1)])
  );
}

function scrubSignedUrlsFromString(value: string): string {
  const completeUrl = scrubSignedUrl(value);

  if (completeUrl !== value) {
    return completeUrl;
  }

  return value.replace(URL_IN_TEXT_PATTERN, match => scrubSignedUrl(match));
}

function scrubSignedUrl(value: string): string {
  try {
    const url = new URL(value);

    if (!isSignedUrl(url)) {
      return value;
    }

    url.search = SCRUBBED_SIGNED_URL_QUERY;
    url.hash = '';

    return url.toString();
  } catch {
    return value;
  }
}

function isSignedUrl(url: URL): boolean {
  const paramNames: string[] = [];
  url.searchParams.forEach((_value, key) => {
    paramNames.push(key.toLowerCase());
  });

  return paramNames.some(paramName => SIGNED_URL_PARAM_NAMES.has(paramName))
    || (isFirebaseStorageUrl(url) && paramNames.includes('token'));
}

function isFirebaseStorageUrl(url: URL): boolean {
  const host = url.hostname.toLowerCase();

  return host === 'firebasestorage.googleapis.com'
    || host.endsWith('.firebasestorage.app');
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
